import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, OnInit, Output, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ItemDescriptionTextPipe } from '@modules/common/pipes/item-description-text.pipe';
import { DialogFocusDirective } from './dialog-focus.directive';
import { IconComponent } from './icon.component';
import { STARTERS, type TemplateChoice } from './new-workbook';
import { SheetsService } from './services/sheets.service';
import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';
import { type Sheet } from './types/sheet';

/**
 * "Choose a template", from New Workbook → "From a template" (Wook, 2026-10-06, as the Design Lab has it): the three
 * built-in starters, then the space's own live templates, each with its title and its description's text (a saved one
 * without a description reads "Saved workbook template"). Picking one says which (`chosen`); the caller makes the
 * workbook. Closing it, by its button, Cancel, Escape or the backdrop, makes nothing (`cancelled`).
 *
 * A fixed overlay of its own, as the import dialog is, so it opens from the nav bar without the modal service.
 */
@Component({
  selector: 'app-template-chooser',
  standalone: true,
  imports: [IconComponent, DialogFocusDirective, ItemDescriptionTextPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './template-chooser.component.html',
  styleUrls: ['./template-chooser.component.less', './sheets-buttons.css'],
})
export class TemplateChooserComponent implements OnInit {
  /** NFR-8: the chooser's words, from sheets.strings.ts. */
  protected readonly S = SHEETS_STRINGS;
  protected readonly starters = STARTERS;
  /** The space's live templates; the starters show while they load, and alone if they can't be read. */
  protected readonly templates = signal<readonly Sheet[]>([]);

  @Output() public readonly chosen = new EventEmitter<TemplateChoice>();
  @Output() public readonly cancelled = new EventEmitter<void>();

  private readonly sheetsService = inject(SheetsService, { optional: true });
  private readonly destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.sheetsService
      ?.liveTemplates()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (templates) => this.templates.set(templates), error: () => undefined });
  }
}
